<template>
  <v-card class="nd-border" flat>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-subtitle>{{ subtitle }}</v-card-subtitle>

    <v-stepper v-model="step" class="official-signup-wizard elevation-0" alt-labels>
      <v-stepper-header class="elevation-0">
        <v-stepper-step :complete="step > 1" step="1" color="primary">Antes de começar</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" step="2" color="primary">Conectar</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step === 3" step="3" color="primary">Pronto</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <div class="wizard-section">
            <v-progress-circular v-if="loadingChips" indeterminate color="primary" />

            <template v-else>
              <v-alert v-if="chipNumbers.length === 0" type="info" text dense>
                Nenhum número da API Oficial aguardando conexão. Crie um novo número em Meus chips para continuar.
                <div class="mt-2">
                  <ib-btn small color="primary" @click.native="$router.push({ name: 'ChipNumbers' })"><v-icon small left>mdi-plus</v-icon>Criar número</ib-btn>
                </div>
              </v-alert>

              <ib-select v-else v-model="selectedChipId" :items="chipNumbers" item-text="_label" item-value="_id" label="Número que será conectado" hide-details="auto" class="wizard-chip-select" />
            </template>

            <div class="text-subtitle-2 mt-6 mb-1">Antes de começar, confirme:</div>
            <v-checkbox v-for="(item, index) in requirements" :key="`requirement_${index}`" v-model="checkedRequirements" :value="index" :label="item" color="primary" hide-details dense class="mt-1" />

            <v-alert v-if="notice" type="warning" :icon="false" text dense class="mt-6 mb-0">
              <div class="font-weight-semibold mb-1">{{ notice.title }}</div>
              <ul class="pl-4">
                <li v-for="(item, index) in notice.items" :key="`notice_${index}`">{{ item }}</li>
              </ul>
            </v-alert>
          </div>

          <div class="wizard-actions">
            <ib-btn text color="primary" @click.native="callSupport">Prefiro falar com o suporte</ib-btn>
            <v-spacer />
            <ib-btn color="primary" :disabled="!canContinue" @click.native="step = 2">Continuar</ib-btn>
          </div>
        </v-stepper-content>

        <v-stepper-content step="2">
          <div class="wizard-section">
            <div class="text-body-1 mb-2">Ao clicar em "Continuar com o Facebook", uma janela da Meta vai abrir. Faça todo o processo nela e não feche esta página.</div>
            <div class="text-body-2 mb-1">Número que será conectado: <b>{{ selectedChip?._label }}</b></div>
            <div class="text-caption">Dica: entre no Facebook neste navegador antes de começar, com a conta administradora do portfólio. Assim a Meta pula a tela de login.</div>
            <div class="text-caption">Se a janela não abrir, libere os pop-ups deste site no navegador e tente de novo.</div>

            <div class="d-flex flex-wrap align-center mt-6 mb-4" style="gap: 12px;">
              <div>
                <div class="text-subtitle-2">Passo a passo na janela da Meta</div>
                <div class="text-body-2">Estas são as telas que vão aparecer. A ordem pode variar um pouco.</div>
              </div>
              <v-spacer />
              <ib-btn v-if="videoUrl" small @click.native="showVideo = true"><v-icon small left>mdi-play-circle-outline</v-icon>Assistir vídeo</ib-btn>
            </div>

            <v-row v-for="(item, index) in steps" :key="`step_${index}`" class="wizard-step" align="center">
              <v-col cols="12" md="5">
                <div class="d-flex align-start">
                  <v-avatar size="28" color="primary" class="white--text text-body-2 mr-3 flex-shrink-0">{{ index + 1 }}</v-avatar>
                  <div>
                    <div class="font-weight-semibold">{{ item.title }}</div>
                    <div class="text-body-2 mt-1">{{ item.text }}</div>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="7">
                <v-img v-if="item.image" :src="item.image" :class="['wizard-image', 'cursor-pointer', { 'wizard-image-phone': item.device === 'phone' }]" contain @click="previewImage = item.image" />
                <div v-else :class="['wizard-placeholder', { 'wizard-placeholder-phone': item.device === 'phone' }]">
                  <v-icon color="gray400">{{ item.device === 'phone' ? 'mdi-cellphone' : 'mdi-image-outline' }}</v-icon>
                  <div class="text-caption mt-1">Print em breve: {{ item.imageHint }}</div>
                </div>
              </v-col>
            </v-row>
          </div>

          <div class="wizard-connect-bar">
            <v-alert v-if="loading && !finishing" type="info" text dense class="mb-3">
              Janela da Meta aberta. Para ver o passo a passo ao mesmo tempo, arraste a janela da Meta para o lado. Se ela sumir atrás desta página, clique nela na barra de tarefas (ou use Alt+Tab).
            </v-alert>

            <v-alert v-if="finishing" type="info" text dense class="mb-3">
              Finalizando a configuração do seu número. Isso leva alguns segundos.
            </v-alert>

            <v-alert v-if="cancelledStep" type="warning" text dense class="mb-3">
              Você fechou a janela da Meta antes de concluir. Clique em "Continuar com o Facebook" para tentar de novo.
              <div class="text-caption mt-1">Etapa em que parou: {{ cancelledStep }}</div>
            </v-alert>

            <v-alert v-if="errorMessage" type="error" text dense class="mb-3">
              {{ errorMessage }}
              <div class="mt-2">
                <ib-btn small text color="error" @click.native="callSupport">Falar com o suporte</ib-btn>
              </div>
            </v-alert>

            <div class="wizard-actions">
              <ib-btn color="gray200" outlined :disabled="loading" @click.native="step = 1">
                <div class="gray1000--text">Voltar</div>
              </ib-btn>
              <v-spacer />
              <ib-btn color="#1877f2" class="white--text text-none" depressed large :loading="loading" @click="launchSignup">
                <v-icon left>mdi-facebook</v-icon>Continuar com o Facebook
              </ib-btn>
            </div>
          </div>
        </v-stepper-content>

        <v-stepper-content step="3">
          <div v-if="result" class="wizard-section">
            <div class="d-flex align-center mb-6">
              <v-icon color="primary" size="40" class="mr-3">mdi-check-circle</v-icon>
              <div>
                <div class="text-h6">Número {{ result.display_phone_number }} conectado!</div>
                <div class="text-body-2">Seu número já pode enviar e receber mensagens pela InteligênciaBot.</div>
              </div>
            </div>

            <v-alert v-if="coex" type="info" text dense>
              O app WhatsApp Business no celular continua funcionando. O que você enviar por ele também aparece aqui.
            </v-alert>

            <div class="text-subtitle-2 mb-2">Próximos passos</div>

            <div class="wizard-next-step">
              <div>
                <div class="font-weight-semibold">1. Cadastre uma forma de pagamento na Meta</div>
                <div class="text-body-2">As mensagens enviadas pela API Oficial são cobradas pela Meta. Cadastre o cartão no Gerenciador do WhatsApp.</div>
              </div>
              <ib-btn small outlined color="primary" :href="whatsappManagerUrl" target="_blank"><v-icon small left>mdi-open-in-new</v-icon>Abrir Gerenciador do WhatsApp</ib-btn>
            </div>

            <div class="wizard-next-step">
              <div>
                <div class="font-weight-semibold">2. Revise o primeiro atendimento do canal</div>
                <div class="text-body-2">Confira o chatbot ou o departamento que vai receber as conversas deste número.</div>
              </div>
              <ib-btn small color="primary" @click.native="$router.push({ name: 'ChipNumbers' })">Ir para Meus chips</ib-btn>
            </div>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <ib-dialog :value="!!previewImage" width="auto" @input="previewImage = null">
      <v-card class="d-flex justify-center pa-2">
        <img :src="previewImage" class="wizard-preview-image">
      </v-card>
    </ib-dialog>

    <video-dialog v-if="videoUrl" v-model="showVideo" :video-url="videoUrl" />
  </v-card>
</template>

<script>
import Api from "@/lib/Api";
import { emitToastr } from "@/lib/Utils";
import VideoDialog from "@/components/VideoDialog.vue";

const META_CONFIG_ID = "1176745574137625";
const WHATSAPP_MANAGER_URL = "https://business.facebook.com/wa/manage/home/";
const SUPPORT_URL = "https://api.whatsapp.com/send?phone=5518981240097&text=Preciso de ajuda para conectar meu número na API Oficial";

export default {
  name: "OfficialSignupWizard",

  components: {
    VideoDialog
  },

  props: {
    coex: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    subtitle: {
      type: String,
      default: ""
    },
    requirements: {
      type: Array,
      default: () => []
    },
    steps: {
      type: Array,
      default: () => []
    },
    notice: {
      type: Object,
      default: null
    },
    videoUrl: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      step: 1,
      loadingChips: true,
      chipNumbers: [],
      selectedChipId: null,
      checkedRequirements: [],
      loading: false,
      finishing: false,
      sessionInfo: null,
      cancelledStep: "",
      errorMessage: "",
      result: null,
      previewImage: null,
      showVideo: false,
      whatsappManagerUrl: WHATSAPP_MANAGER_URL
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    selectedChip() {
      return this.chipNumbers.find(c => c._id === this.selectedChipId);
    },

    canContinue() {
      return !!this.selectedChipId && this.checkedRequirements.length === this.requirements.length;
    }
  },

  async mounted() {
    if (!this.currentUser?.access_areas?.includes('chips')) {
      emitToastr("error", "Permissões insuficientes.");
      this.$router.replace({ name: 'Atendimento' });
      return;
    }

    window.addEventListener('message', this.handleSessionMessage);
    await this.loadChipNumbers();
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleSessionMessage);
  },

  methods: {
    async loadChipNumbers() {
      this.loadingChips = true;
      const resp = await Api.getChipNumbersByUser();

      if (resp && !resp.error && Array.isArray(resp.message)) {
        this.chipNumbers = resp.message
          .filter(c => c.official === true && c.status !== 'open' && c.registered !== true)
          .map(c => ({ ...c, _label: c.name || c.display_phone_number }));
      }

      const queryChip = this.$route.query.chip;
      if (queryChip && this.chipNumbers.some(c => c._id === queryChip)) this.selectedChipId = queryChip;
      else if (this.chipNumbers.length === 1) this.selectedChipId = this.chipNumbers[0]._id;

      this.loadingChips = false;
    },

    handleSessionMessage(event) {
      if (!event.origin || !event.origin.endsWith('facebook.com')) return;

      let payload = event.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { return; }
      }

      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

      const data = payload.data || {};
      const eventName = payload.event || data.event;

      if (typeof eventName === 'string' && eventName.startsWith('FINISH')) {
        this.sessionInfo = { waba_id: data.waba_id, phone_number_id: data.phone_number_id };
        return;
      }

      if (data.error_message) {
        this.cancelledStep = "";
        this.errorMessage = `A Meta retornou um erro: ${data.error_message}`;
        this.loading = false;
        return;
      }

      if (eventName === 'CANCEL') {
        this.errorMessage = "";
        this.cancelledStep = data.current_step || "não informada";
        this.loading = false;
      }
    },

    launchSignup() {
      if (!window.FB) {
        this.errorMessage = "Não foi possível carregar o login do Facebook. Desative bloqueadores de anúncio e recarregue a página.";
        return;
      }

      this.loading = true;
      this.finishing = false;
      this.cancelledStep = "";
      this.errorMessage = "";
      this.sessionInfo = null;

      const extras = this.coex ? { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' } : { setup: {} };

      window.FB.login((response) => { this.finishSignup(response); }, {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras
      });
    },

    async finishSignup(response) {
      const code = response?.authResponse?.code;

      if (!code) {
        if (!this.cancelledStep && !this.errorMessage) this.errorMessage = "A conexão com a Meta não foi concluída. Tente novamente.";
        this.loading = false;
        return;
      }

      this.finishing = true;

      const resp = await Api.officialSignup({
        code,
        chipNumberId: this.selectedChipId,
        waba_id: this.sessionInfo?.waba_id,
        phone_number_id: this.sessionInfo?.phone_number_id,
        coex: this.coex
      });

      this.finishing = false;
      this.loading = false;

      if (!resp || resp.error) {
        this.errorMessage = resp?.message || "Não foi possível concluir a conexão. Tente novamente.";
        return;
      }

      this.result = resp.message;
      this.step = 3;
    },

    callSupport() {
      window.open(SUPPORT_URL, "_blank");
    }
  }
};
</script>

<style lang="scss" scoped>
.official-signup-wizard {
  background: transparent !important;
}

.official-signup-wizard,
.official-signup-wizard ::v-deep .v-stepper__items,
.official-signup-wizard ::v-deep .v-stepper__wrapper {
  overflow: clip;
}

.official-signup-wizard ::v-deep .v-stepper__label {
  white-space: nowrap;
}

.wizard-section {
  max-width: 960px;
  padding-top: 8px;
}

.wizard-chip-select {
  max-width: 420px;
}

.wizard-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.wizard-connect-bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: 24px;
  padding: 12px 0;
  border-top: 1px solid var(--v-gray100-base);
  background: #fff;
}

.theme--dark .wizard-connect-bar {
  background: var(--v-gray50-base);
}

.wizard-connect-bar .wizard-actions {
  margin-top: 0;
}

.wizard-step + .wizard-step {
  border-top: 1px solid var(--v-gray100-base);
}

.wizard-image {
  border: 1px solid var(--v-gray100-base);
  border-radius: 10px;
  max-height: 360px;
}

.wizard-image-phone {
  max-width: 240px;
}

.wizard-preview-image {
  display: block;
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
}

.wizard-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  aspect-ratio: 16 / 9;
  max-width: 100%;
  border: 1.5px dashed var(--v-gray200-base);
  border-radius: 10px;
  color: var(--v-gray500-base);
}

.wizard-placeholder-phone {
  aspect-ratio: 9 / 16;
  max-width: 200px;
}

.wizard-next-step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 10px;
  border: 1px solid var(--v-gray100-base);
  border-radius: 10px;
}
</style>
